import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { toast } from 'react-toastify';
import { ResponsiveLine, Serie } from '@nivo/line';
import DateRangeSelector from '../../../components/DateRangeSelector';
import countryNames from '../../../constants/countryNames';
import {
  getProfitAndLoss,
  PnLCountryData,
  PnLMonthData,
  PnLResponse,
  PnLTotalData,
} from '../services/DashboardCRUD';

type PnLField = keyof PnLCountryData;

const PNL_FIELDS: { field: PnLField; i18nKey: string; sign: 1 | -1 }[] = [
  {
    field: 'income_from_product_sales',
    i18nKey: 'pnl.incomeFromProductSales',
    sign: 1,
  },
  {
    field: 'income_from_shipment_services',
    i18nKey: 'pnl.incomeFromShipmentServices',
    sign: 1,
  },
  { field: 'discounts', i18nKey: 'pnl.discounts', sign: -1 },
  { field: 'adj_to_listed_prices', i18nKey: 'pnl.adjToListedPrices', sign: -1 },
  { field: 'promos', i18nKey: 'pnl.promos', sign: -1 },
  { field: 'performance_bonuses', i18nKey: 'pnl.performanceBonuses', sign: -1 },
  {
    field: 'store_related_monthly_costs',
    i18nKey: 'pnl.storeRelatedMonthlyCosts',
    sign: -1,
  },
  { field: 'sales_cancellation', i18nKey: 'pnl.salesCancellation', sign: -1 },
  {
    field: 'misc_custom_invoices',
    i18nKey: 'pnl.miscCustomInvoices',
    sign: 1,
  },
];

function val(data: PnLCountryData, field: PnLField): number {
  return parseFloat(data[field] || '0');
}

// Fields whose displayed sign is flipped relative to the backend value.
// The backend returns sales_cancellation as a positive figure for actual
// cancellations (revenue clawed back), but we show it as a negative amount so it
// reads as a reduction of sales. This is display-only: computeNetRevenue keeps
// using the raw value and the field `sign`, so the Net income is unchanged.
const DISPLAY_INVERTED_FIELDS: PnLField[] = ['sales_cancellation'];

function displayVal(data: PnLCountryData, field: PnLField): number {
  const raw = val(data, field);
  return DISPLAY_INVERTED_FIELDS.includes(field) ? -raw : raw;
}

function computeNetRevenue(data: PnLCountryData): number {
  return PNL_FIELDS.reduce(
    (acc, { field, sign }) => acc + sign * val(data, field),
    0
  );
}

function fieldGetValue(field: PnLField) {
  return (data: PnLCountryData) => displayVal(data, field);
}

function computeRevenuePlusShippingMinusCancellations(
  data: PnLCountryData
): number {
  return (
    val(data, 'income_from_product_sales') +
    val(data, 'income_from_shipment_services') -
    val(data, 'sales_cancellation')
  );
}

function formatMonth(monthStr: string): string {
  return moment(monthStr, 'YYYY-MM').format('MMM YYYY');
}

function formatCurrency(
  intl: ReturnType<typeof useIntl>,
  value: number
): string {
  return intl.formatNumber(value, { style: 'currency', currency: 'EUR' });
}

// --- Summary line chart ---

function SummaryChart({
  months,
  intl,
}: {
  months: PnLMonthData[];
  intl: ReturnType<typeof useIntl>;
}) {
  const series: Serie[] = [
    {
      id: intl.formatMessage({ id: 'pnl.incomeFromProductSales' }),
      data: months.map((m) => ({
        x: m.month,
        y: val(m, 'income_from_product_sales'),
      })),
    },
    {
      id: intl.formatMessage({ id: 'pnl.netRevenue' }),
      data: months.map((m) => ({ x: m.month, y: computeNetRevenue(m) })),
    },
  ];

  const allValues = series.flatMap((s) => s.data.map((d) => d.y as number));
  const maxValue = Math.max(...allValues) * 1.2;
  const minValue = Math.min(...allValues, 0);

  return (
    <div style={{ height: 300 }} className="mb-10 p-4 bg-light rounded">
      <ResponsiveLine
        data={series}
        margin={{ top: 20, right: 180, bottom: 40, left: 80 }}
        xScale={{ type: 'point' }}
        yScale={{
          type: 'linear',
          min: minValue < 0 ? minValue * 1.1 : 0,
          max: maxValue,
          stacked: false,
        }}
        axisBottom={{
          tickSize: 0,
          tickPadding: 8,
          format: (v: string) => formatMonth(v),
        }}
        axisLeft={{
          tickSize: 0,
          tickPadding: 8,
          tickValues: 5,
          format: (v: number) => formatCurrency(intl, v),
        }}
        gridYValues={5}
        enableGridX={false}
        theme={{
          grid: { line: { stroke: '#e8e8e8', strokeDasharray: '4 4' } },
        }}
        pointSize={10}
        pointColor="white"
        pointBorderWidth={2}
        pointBorderColor={{ from: 'serieColor' }}
        enableSlices="x"
        sliceTooltip={({ slice }) => (
          <div className="card p-3 shadow">
            <div className="fw-bold mb-2">
              {formatMonth(String(slice.points[0]?.data.x))}
            </div>
            {slice.points.map((point) => (
              <div key={point.id} className="d-flex align-items-center mt-1">
                <span
                  className="rounded-circle me-2 d-inline-block"
                  style={{
                    width: 10,
                    height: 10,
                    backgroundColor: point.serieColor,
                  }}
                />
                <span className="me-2">{point.serieId}:</span>
                <span className="fw-bold">
                  {formatCurrency(intl, point.data.y as number)}
                </span>
              </div>
            ))}
          </div>
        )}
        legends={[
          {
            toggleSerie: true,
            anchor: 'right',
            direction: 'column',
            translateX: 170,
            itemWidth: 150,
            itemHeight: 20,
            symbolSize: 12,
            symbolShape: 'circle',
          },
        ]}
        colors={['#009ef7', '#50cd89']}
        curve="monotoneX"
        useMesh={true}
      />
    </div>
  );
}

// --- Mini line chart for expandable rows ---

function MiniLineChart({
  months,
  getValue,
  label,
  intl,
}: {
  months: PnLMonthData[];
  getValue: (data: PnLCountryData) => number;
  label: string;
  intl: ReturnType<typeof useIntl>;
}) {
  const series: Serie[] = [
    {
      id: label,
      data: months.map((m) => ({ x: m.month, y: getValue(m) })),
    },
  ];

  const values = series[0].data.map((d) => d.y as number);
  const maxVal = Math.max(...values) * 1.2 || 1;
  const minVal = Math.min(...values, 0);

  return (
    <div style={{ height: 160 }} className="m-3 p-3 bg-white rounded">
      <ResponsiveLine
        data={series}
        margin={{ top: 10, right: 20, bottom: 30, left: 70 }}
        xScale={{ type: 'point' }}
        yScale={{
          type: 'linear',
          min: minVal < 0 ? minVal * 1.1 : 0,
          max: maxVal,
          stacked: false,
        }}
        axisBottom={{
          tickSize: 0,
          tickPadding: 8,
          format: (v: string) => formatMonth(v),
        }}
        axisLeft={{
          tickSize: 0,
          tickPadding: 8,
          tickValues: 3,
          format: (v: number) => formatCurrency(intl, v),
        }}
        gridYValues={3}
        theme={{
          grid: { line: { stroke: '#e8e8e8', strokeDasharray: '4 4' } },
        }}
        pointSize={8}
        pointColor="white"
        pointBorderWidth={2}
        pointBorderColor={{ from: 'serieColor' }}
        enableSlices="x"
        sliceTooltip={({ slice }) => (
          <div className="card p-2 shadow small">
            <span className="fw-bold">
              {formatMonth(String(slice.points[0]?.data.x))}:{' '}
            </span>
            <span>
              {formatCurrency(intl, slice.points[0]?.data.y as number)}
            </span>
          </div>
        )}
        colors={['#009ef7']}
        curve="monotoneX"
        useMesh={true}
        enableGridX={false}
      />
    </div>
  );
}

// --- Expandable row ---

interface ExpandableRowProps {
  label: string;
  months: PnLMonthData[];
  total: PnLTotalData;
  getValue: (data: PnLCountryData) => number;
  intl: ReturnType<typeof useIntl>;
  bold?: boolean;
}

const ES_PT_AND_CODES = ['ES', 'PT', 'AD'];

function sumCountries(
  countries: Record<string, PnLCountryData>,
  codes: string[],
  getValue: (data: PnLCountryData) => number
): number {
  return codes.reduce(
    (sum, code) => sum + getValue(countries[code] || ({} as PnLCountryData)),
    0
  );
}

function ExpandableRow({
  label,
  months,
  total,
  getValue,
  intl,
  bold,
}: ExpandableRowProps) {
  const [expanded, setExpanded] = useState(false);

  const countryCodes = Object.keys(total.countries).filter((code) => {
    const totalVal = getValue(total.countries[code]);
    return totalVal !== 0;
  });
  const hasDetail = countryCodes.length > 0;
  const colCount = months.length + 2; // concept + months + total

  const esPtAndCodes = ES_PT_AND_CODES.filter(
    (code) => total.countries[code] !== undefined
  );
  const showEsPtAndRow =
    esPtAndCodes.length > 1 &&
    esPtAndCodes.some((code) => getValue(total.countries[code]) !== 0);

  return (
    <>
      <tr
        onClick={hasDetail ? () => setExpanded(!expanded) : undefined}
        style={{ cursor: hasDetail ? 'pointer' : 'default' }}
        className={hasDetail ? 'user-select-none' : ''}
      >
        <td className={`px-4 text-nowrap${bold ? ' fw-bold' : ''}`}>
          {label}
          {hasDetail && (
            <i
              className="bi bi-chevron-down ms-2 text-dark"
              style={{
                transition: 'transform 0.2s ease',
                transform: expanded ? 'rotate(-180deg)' : 'rotate(0deg)',
                display: 'inline-block',
              }}
            />
          )}
        </td>
        {months.map((m) => (
          <td
            key={m.month}
            className={`px-4 text-end text-nowrap${bold ? ' fw-bold' : ''}`}
          >
            {formatCurrency(intl, getValue(m))}
          </td>
        ))}
        <td className="px-4 text-end fw-bold text-nowrap">
          {formatCurrency(intl, getValue(total))}
        </td>
      </tr>
      {expanded && hasDetail && (
        <tr>
          <td colSpan={colCount} className="p-4 bg-light">
            {months.length > 1 && (
              <MiniLineChart
                months={months}
                getValue={getValue}
                label={label}
                intl={intl}
              />
            )}
            <table className="table table-sm table-bordered table-striped mb-0 w-100 mt-4">
              <thead>
                <tr className="fw-bold table-light">
                  <th className="px-4">
                    {intl.formatMessage({ id: 'pnl.countryBreakdown' })}
                  </th>
                  {months.map((m) => (
                    <th key={m.month} className="px-4 text-end text-nowrap">
                      {formatMonth(m.month)}
                    </th>
                  ))}
                  <th className="px-4 text-end text-nowrap">
                    {intl.formatMessage({ id: 'pnl.total' })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {countryCodes
                  .sort(
                    (a, b) =>
                      Math.abs(getValue(total.countries[b])) -
                      Math.abs(getValue(total.countries[a]))
                  )
                  .map((code) => (
                    <tr key={code}>
                      <td className="px-4">
                        {countryNames[code as keyof typeof countryNames] ||
                          code}
                      </td>
                      {months.map((m) => (
                        <td key={m.month} className="px-4 text-end text-nowrap">
                          {formatCurrency(
                            intl,
                            getValue(
                              m.countries[code] || ({} as PnLCountryData)
                            )
                          )}
                        </td>
                      ))}
                      <td className="px-4 text-end fw-bold text-nowrap">
                        {formatCurrency(intl, getValue(total.countries[code]))}
                      </td>
                    </tr>
                  ))}
                {showEsPtAndRow && (
                  <tr className="fw-bold table-info">
                    <td className="px-4">
                      {intl.formatMessage({ id: 'pnl.esPtAndSubtotal' })}
                    </td>
                    {months.map((m) => (
                      <td key={m.month} className="px-4 text-end text-nowrap">
                        {formatCurrency(
                          intl,
                          sumCountries(m.countries, esPtAndCodes, getValue)
                        )}
                      </td>
                    ))}
                    <td className="px-4 text-end text-nowrap">
                      {formatCurrency(
                        intl,
                        sumCountries(total.countries, esPtAndCodes, getValue)
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

// --- Main component ---

export default function PnLReport() {
  const intl = useIntl();
  const MAX_DAYS = 24 * 30;
  const initialSince = moment()
    .subtract(28 * 3, 'days')
    .toDate();
  const initialUntil = moment().subtract(1, 'day').toDate();
  const [since, setSince] = useState<Date>(initialSince);
  const [until, setUntil] = useState<Date>(initialUntil);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PnLResponse | null>(null);

  async function fetchData() {
    const daysDiff = moment(until).diff(moment(since), 'days');
    if (daysDiff > MAX_DAYS) {
      toast.error(
        intl.formatMessage(
          { id: 'businessDashboard.dateRangeExceedsLimit' },
          { maxDays: MAX_DAYS }
        )
      );
      return;
    }
    try {
      setLoading(true);
      const { data: response } = await getProfitAndLoss({
        startDate: moment(since).format('YYYY-MM-DD'),
        endDate: moment(until).format('YYYY-MM-DD'),
      });
      setData(response);
    } catch (error) {
      console.warn(error);
      toast.error(intl.formatMessage({ id: 'pnl.errorFetchingData' }));
    } finally {
      setLoading(false);
    }
  }

  const months = data?.months || [];
  const total = data?.total;
  const colCount = months.length + 2;

  return (
    <>
      <h1 className="title mb-8">{intl.formatMessage({ id: 'pnl.title' })}</h1>
      <div className="d-flex gap-4 align-items-end mb-8">
        <DateRangeSelector
          initialSince={initialSince}
          initialUntil={initialUntil}
          onSelected={(s, u) => {
            setSince(s);
            setUntil(u);
            setData(null);
          }}
          disableFutureDates
        />
        <button
          className="btn btn-primary"
          onClick={fetchData}
          disabled={loading}
        >
          {loading && (
            <span className="spinner-border spinner-border-sm me-2" />
          )}
          {intl.formatMessage({ id: 'pnl.generateReport' })}
        </button>
      </div>

      {loading && (
        <div className="d-flex justify-content-center py-10">
          <span className="spinner-border spinner-border-lg text-primary" />
        </div>
      )}

      {!loading && !total && (
        <p className="text-muted fs-6">
          {intl.formatMessage({ id: 'pnl.noData' })}
        </p>
      )}

      {!loading && total && (
        <>
          {months.length > 1 && <SummaryChart months={months} intl={intl} />}

          <div className="table-responsive">
            <table className="table table-bordered table-striped table-hover">
              <thead className="table-light fw-bold">
                <tr>
                  <th className="px-4">
                    {intl.formatMessage({ id: 'pnl.concept' })}
                  </th>
                  {months.map((m) => (
                    <th key={m.month} className="px-4 text-end text-nowrap">
                      {formatMonth(m.month)}
                    </th>
                  ))}
                  <th className="px-4 text-end text-nowrap">
                    {intl.formatMessage({ id: 'pnl.total' })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {PNL_FIELDS.map(({ field, i18nKey }) => (
                  <ExpandableRow
                    key={field}
                    label={intl.formatMessage({ id: i18nKey })}
                    months={months}
                    total={total}
                    getValue={(data) => displayVal(data, field)}
                    intl={intl}
                  />
                ))}
                <ExpandableRow
                  key="revenue_plus_shipping_minus_cancellations"
                  label={intl.formatMessage({
                    id: 'pnl.revenuePlusShippingMinusCancellations',
                  })}
                  months={months}
                  total={total}
                  getValue={computeRevenuePlusShippingMinusCancellations}
                  intl={intl}
                  bold
                />
              </tbody>
              <tfoot className="table-light fw-bold">
                <ExpandableRow
                  label={intl.formatMessage({ id: 'pnl.netRevenue' })}
                  months={months}
                  total={total}
                  getValue={computeNetRevenue}
                  intl={intl}
                />
              </tfoot>
            </table>
          </div>
        </>
      )}
    </>
  );
}
